import { useMemo, useState } from 'react';
import { ChartColumn } from 'lucide-react';

import { BarChartCard, DonutCard, LineChartCard } from '~/components/dashboard';
import { Card, EmptyState, FiltersButton, LoadError } from '~/components/ui';
import { useListUrlState } from '~/hooks/useListUrlState';
import { useUrlParams } from '~/hooks/useUrlParams';
import { useGetAnalyticsOverviewQuery } from '~/modules/query/ai/analytics';
import {
  getAnalyticsErrorInfo,
  mapAnalyticsOverview,
  resolveDateRangeParams,
  TELEMETRY_STATUSES,
  TELEMETRY_STATUS_DESCRIPTIONS,
} from '~/lib/ai/analytics';
import { formatDuration, formatMoney, formatNumber } from '~/lib/format';
import type { TelemetryStatus } from '~/modules/query/ai/types';
import type { TokenName } from '~/lib/theme-colors';

import {
  ANALYTICS_FILTER_KEYS,
  AnalyticsFilterDrawer,
  AnalyticsSourcePanel,
  OVERVIEW_DATE_PRESETS,
  readAnalyticsScope,
} from '~/components/pages/ai/analytics/AnalyticsFilters';

// A slice's color is its state (DESIGN §8.1): healthy, broken, stopped, attention, in progress.
const STATUS_TOKENS: Record<TelemetryStatus, TokenName> = {
  Completed: 'success-500',
  Abandoned: 'gray-500',
  Active: 'brand-500',
  Error: 'error-500',
  Throttled: 'warning-500',
  Blocked: 'orange-500',
};

const TOKEN_KEYS = [
  { id: 'input', label: 'Input' },
  { id: 'output', label: 'Output', token: 'theme-purple-500' as const },
  { id: 'cached', label: 'Cached', token: 'orange-500' as const },
];

/** Aggregate health for the selected source and filters: tokens, cost, latency, request status (DESIGN §8). */
export default function AnalyticsOverviewTab({ sourceTabs }: { sourceTabs: React.ReactNode }) {
  const url = useUrlParams();
  const list = useListUrlState({ filterKeys: ANALYTICS_FILTER_KEYS });
  const filters = readAnalyticsScope(url.get, OVERVIEW_DATE_PRESETS);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { data, isLoading, isError, error, refetch } = useGetAnalyticsOverviewQuery({
    source: filters.source,
    ...resolveDateRangeParams(filters.dateRange),
    placementKeys: filters.source === 'placement' && filters.entity ? [filters.entity] : undefined,
    assistantKeys:
      filters.source === 'digital_assistant' && filters.entity ? [filters.entity] : undefined,
    models: filters.model ? [filters.model] : undefined,
  });

  const stats = useMemo(() => (data ? mapAnalyticsOverview(data) : undefined), [data]);
  const hasData =
    !!stats &&
    (stats.tokenConsumption.length > 0 ||
      stats.estimatedCost.length > 0 ||
      stats.averageLatency.length > 0 ||
      Object.values(stats.requestStatusBreakdown).some((count) => count > 0));

  const errorInfo = isError ? getAnalyticsErrorInfo(error) : null;

  const tokens = stats?.tokenConsumption ?? [];
  const tokenRows = tokens.map((point) => ({
    label: point.intervalLabel,
    input: point.inputTokens,
    output: point.outputTokens,
    cached: point.cachedTokens,
  }));
  const tokenTotals = tokens.reduce(
    (sum, point) => ({
      input: sum.input + point.inputTokens,
      output: sum.output + point.outputTokens,
      cached: sum.cached + point.cachedTokens,
    }),
    { input: 0, output: 0, cached: 0 },
  );
  const statusSlices = TELEMETRY_STATUSES.map((status) => ({
    id: status,
    label: status,
    value: stats?.requestStatusBreakdown[status] ?? 0,
    token: STATUS_TOKENS[status],
    description: TELEMETRY_STATUS_DESCRIPTIONS[status],
  }));
  const cost = stats?.estimatedCost ?? [];
  const costPoints = cost.map((point) => ({ x: point.intervalLabel, y: point.estimatedCostUsd }));
  const costTotal = cost.reduce((sum, point) => sum + point.estimatedCostUsd, 0);
  const costPeak = cost.length
    ? cost.reduce((top, point) => (point.estimatedCostUsd > top.estimatedCostUsd ? point : top))
    : undefined;
  const latency = stats?.averageLatency ?? [];
  const latencyPoints = latency.map((point) => ({
    x: point.intervalLabel,
    y: point.avgLatencyMs / 1000,
  }));
  const latencyAverage = latency.length
    ? latency.reduce((sum, point) => sum + point.avgLatencyMs, 0) / latency.length
    : 0;
  const latencyPeak = latency.length
    ? latency.reduce((top, point) => (point.avgLatencyMs > top.avgLatencyMs ? point : top))
    : undefined;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {sourceTabs}
        <FiltersButton
          count={filters.activeCount - (filters.status ? 1 : 0)}
          onClick={() => setIsFilterOpen(true)}
          onClear={list.clear}
          testId="ai-analytics-filters"
        />
      </div>

      <AnalyticsFilterDrawer
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        source={filters.source}
        value={list.filters}
        onApply={list.setFilters}
        onClear={list.clear}
        showStatus={false}
        datePresets={OVERVIEW_DATE_PRESETS}
      />

      <AnalyticsSourcePanel source={filters.source} className="space-y-6">
        {errorInfo ? (
          <LoadError
            what="the analytics overview"
            message={errorInfo.message}
            onRetry={() => refetch()}
            testId="ai-analytics-overview"
          />
        ) : null}

        {!errorInfo && !isLoading && !hasData ? (
          <Card>
            <EmptyState
              icon={<ChartColumn className="size-8" aria-hidden="true" />}
              title="No telemetry in this range"
              description={`Widen the date range or clear a filter to see ${filters.source === 'placement' ? 'placement' : 'digital assistant'} usage.`}
            />
          </Card>
        ) : isLoading || hasData ? (
          <section aria-label="Analytics overview" className="grid grid-cols-12 gap-4 md:gap-6">
            <BarChartCard
              className="col-span-12 xl:col-span-7"
              title="Token consumption"
              description="Input, output and cached tokens per interval."
              ariaLabel="Token consumption over time, stacked by input, output and cached tokens"
              loading={isLoading}
              rows={tokenRows}
              keys={TOKEN_KEYS}
              summary={`${formatNumber(tokenTotals.input + tokenTotals.output + tokenTotals.cached)} tokens in total: ${formatNumber(tokenTotals.input)} input, ${formatNumber(tokenTotals.output)} output and ${formatNumber(tokenTotals.cached)} cached.`}
              emptyTitle="No token telemetry in this range"
              emptyDescription="Tokens appear here once a placement or digital assistant has been used."
            />
            <DonutCard
              className="col-span-12 xl:col-span-5"
              title="Request status"
              description="How every request in this range ended."
              ariaLabel="Request status breakdown: completed, abandoned, active, error, throttled and blocked"
              loading={isLoading}
              slices={statusSlices}
              unit={{ one: 'request', other: 'requests' }}
              emptyTitle="No requests in this range"
              emptyDescription="Request statuses appear once a placement or digital assistant has been used."
            />
            <LineChartCard
              className="col-span-12 lg:col-span-6"
              title="Estimated cost"
              description="What the requests in this range cost at the configured model prices."
              ariaLabel="Estimated cost over time in US dollars"
              loading={isLoading}
              series={[{ id: 'Estimated cost', data: costPoints }]}
              valueFormat="$,.2f"
              summary={
                costPeak &&
                `${formatMoney(costTotal)} total; highest interval ${costPeak.intervalLabel} at ${formatMoney(costPeak.estimatedCostUsd)}.`
              }
              emptyTitle="No cost data in this range"
              emptyDescription="Costs appear once a model has pricing configured and requests have run."
            />
            <LineChartCard
              className="col-span-12 lg:col-span-6"
              title="Average latency"
              description="How long a request took, averaged per interval."
              ariaLabel="Average latency over time in seconds"
              loading={isLoading}
              series={[{ id: 'Average latency', data: latencyPoints }]}
              seriesTokens={['theme-purple-500']}
              valueFormat=" >-,.1f"
              summary={
                latencyPeak &&
                `${formatDuration(latencyAverage)} average; slowest interval ${latencyPeak.intervalLabel} at ${formatDuration(latencyPeak.avgLatencyMs)}.`
              }
              emptyTitle="No latency data in this range"
              emptyDescription="Latency appears once requests have run against a placement or digital assistant."
            />
          </section>
        ) : null}
      </AnalyticsSourcePanel>
    </div>
  );
}
